import type { ReactNode } from 'react'

import { normalizeFirstName, normalizeLastName } from '@massilia/features'
import { cn } from '@massilia/ui'
import { Button } from '@massilia/ui'
import { Calendar, Check, Pencil } from 'lucide-react'
import { useState } from 'react'

import type { ReservationPassengerSchema } from '@/lib/schemas/reservation/reservation.schema'

import { formatDateLong } from '@/utils/format-date'

import { GENDER_COLORS, GENDER_LABELS } from '../-utils/constants'

interface PassengerCardProps {
  children: ReactNode
  /** Form fields for editing firstName, lastName, and birthDate — rendered only in edit mode */
  editFields: ReactNode
  index: number
  passenger: ReservationPassengerSchema
}

export function PassengerCard({ children, editFields, index, passenger }: PassengerCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const initials = getInitials(passenger.firstName, passenger.lastName)
  const genderColor = GENDER_COLORS[passenger.gender] || GENDER_COLORS.MALE

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl border transition-all duration-300',
        'bg-white hover:shadow-md'
      )}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="p-4 sm:p-5">
        {/* Header with avatar and basic info */}
        <div className="mb-4 flex items-start gap-4">
          {/* Avatar */}
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg',
              'bg-linear-to-br shadow-sm',
              'transition-transform duration-300 group-hover:scale-105',
              genderColor
            )}
          >
            <span className="text-sm font-bold text-white">{initials}</span>
          </div>

          {/* Name and details */}
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 text-xs font-medium tracking-wider text-stone-400 uppercase">
              Passager {index + 1}
            </div>

            {isEditing ? (
              <div className="mt-1">{editFields}</div>
            ) : (
              <>
                <h4 className="truncate font-semibold text-stone-800">
                  {GENDER_LABELS[passenger.gender]} {normalizeFirstName(passenger.firstName)} {normalizeLastName(passenger.lastName)}
                </h4>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-stone-500">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{formatDateLong(passenger.birthDate)}</span>
                </div>
              </>
            )}
          </div>

          {/* Edit / Done button */}
          <Button
            className="h-7 shrink-0 gap-1 text-xs text-blue-600 hover:text-blue-800"
            onClick={() => setIsEditing((v) => !v)}
            size="sm"
            type="button"
            variant="ghost"
          >
            {isEditing ? (
              <>
                <Check className="size-3.5" />
                Valider
              </>
            ) : (
              <>
                <Pencil className="size-3.5" />
                Modifier
              </>
            )}
          </Button>
        </div>

        {/* Divider */}
        <div className="mb-4 border-t border-dashed border-stone-200" />

        {/* Editable fields - rendered via children */}
        {children}
      </div>
    </div>
  )
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}
